import React, { useEffect, useRef } from 'react';
import { useDraggable } from '@/hooks/useDraggable';
import chatRobot from '@/imgs/chat-robot.gif';

interface DraggableChatButtonProps {
  /**
   * 点击事件处理函数
   */
  onClick?: () => void;
  /**
   * 自定义类名
   */
  className?: string;
  /**
   * 初始位置
   */
  initialPosition?: { x: number; y: number };
  /**
   * 边界容器选择器
   */
  boundarySelector?: string;
}

/**
 * 可拖拽的聊天按钮组件
 * 支持拖拽移动，并自动限制在边界内
 */
const DraggableChatButton: React.FC<DraggableChatButtonProps> = ({
  onClick,
  className = '',
  initialPosition,
  boundarySelector
}) => {
  const { elementRef, position, isDragging, handleMouseDown, handleTouchStart } = useDraggable({
    initialPosition,
    boundarySelector,
    enabled: true
  });

  // 用于区分拖拽和点击
  const dragStartPositionRef = useRef<{ x: number; y: number } | null>(null);
  const hasMovedRef = useRef(false);

  // 处理点击事件，只有在没有拖拽的情况下才触发
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // 如果发生了拖拽，不触发点击事件
    if (hasMovedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      hasMovedRef.current = false;
      dragStartPositionRef.current = null;
      return;
    }
    onClick?.();
  };

  // 处理触摸点击事件
  const handleTouchClick = (e: React.TouchEvent<HTMLDivElement>) => {
    // 如果发生了拖拽，不触发点击事件
    if (hasMovedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      hasMovedRef.current = false;
      dragStartPositionRef.current = null;
      return;
    }
    onClick?.();
  };

  // 包装鼠标按下事件，记录初始位置
  const handleMouseDownWithTracking = (e: React.MouseEvent<HTMLDivElement>) => {
    dragStartPositionRef.current = { x: e.clientX, y: e.clientY };
    hasMovedRef.current = false;
    handleMouseDown(e);

    // 监听鼠标移动，判断是否发生了拖拽
    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (dragStartPositionRef.current) {
        const deltaX = Math.abs(moveEvent.clientX - dragStartPositionRef.current.x);
        const deltaY = Math.abs(moveEvent.clientY - dragStartPositionRef.current.y);
        // 如果移动距离超过5px，认为是拖拽
        if (deltaX > 5 || deltaY > 5) {
          hasMovedRef.current = true;
        }
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // 包装触摸开始事件，记录初始位置
  const handleTouchStartWithTracking = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      dragStartPositionRef.current = { x: touch.clientX, y: touch.clientY };
      hasMovedRef.current = false;
      handleTouchStart(e);

      // 监听触摸移动，判断是否发生了拖拽
      const handleTouchMove = (moveEvent: TouchEvent) => {
        if (moveEvent.touches.length === 1 && dragStartPositionRef.current) {
          const touch = moveEvent.touches[0];
          const deltaX = Math.abs(touch.clientX - dragStartPositionRef.current.x);
          const deltaY = Math.abs(touch.clientY - dragStartPositionRef.current.y);
          // 如果移动距离超过5px，认为是拖拽
          if (deltaX > 5 || deltaY > 5) {
            hasMovedRef.current = true;
          }
        }
      };

      const handleTouchEnd = () => {
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        document.removeEventListener('touchcancel', handleTouchEnd);
      };

      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      document.addEventListener('touchcancel', handleTouchEnd);
    }
  };

  const handleResize = () => {
    if (elementRef.current) {
      const elStyle = window.getComputedStyle(elementRef.current);
      const left = elStyle.left;
      if (left && parseInt(left)) {
        elementRef.current.style.left = window.innerWidth - elementRef.current.offsetWidth + 'px';
      }
    }
  };

  useEffect(() => {
    window?.addEventListener('resize', handleResize);
    return () => {
      window?.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div
      ref={elementRef}
      className={`fixed z-[1] flex h-[52px] w-[52px] cursor-pointer touch-none items-center rounded-full p-[1px] transition-opacity ${
        isDragging ? 'opacity-80' : 'opacity-100'
      } ${className}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translate(0, 0)',
        touchAction: 'none' // 防止移动端的默认触摸行为
      }}
      onMouseDown={handleMouseDownWithTracking}
      onTouchStart={handleTouchStartWithTracking}
      onClick={handleClick}
      onTouchEnd={handleTouchClick}
    >
      {/* AI对话提示标签 */}
      <div className="absolute right-[10px] top-[-16px] z-[11] flex h-5 items-center rounded-sm bg-white px-[3px] text-xs text-white">
        <span className="ai-chat-text">AI对话</span>
        <div className="absolute right-[2px] top-[20px] z-[11] h-0 w-0 border-x-4 border-t-4 border-x-transparent border-t-white"></div>
      </div>

      {/* 机器人图标 */}
      <div className="flex h-[52px] w-[52px]">
        <img src={chatRobot} alt="AI对话" width={52} height={52} className="scale-[1.2]" />
      </div>
    </div>
  );
};

export default DraggableChatButton;
